import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { api, STATUS_LABELS, STATUS_ORDER } from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../context/SettingsContext";
import { SkuCombobox } from "./SkuCombobox";
import { QuickAddSkuDialog } from "./QuickAddSkuDialog";
import { Flame } from "lucide-react";

const EMPTY = {
  supplier_po: "",
  internal_po: "",
  revised_po: "",
  sales_order: "",
  customer_ref: "",
  product_sku: "",
  alternate_sku: "",
  description: "",
  barcode: "",
  status: "awaiting_arrival",
  location: "",
  third_party_id: "",
  notes: "",
  brightpearl_id: "",
  is_urgent: false,
  urgent_reason: "",
  customer_required_date: "",
};

export const CreateRecordDialog = ({ open, onOpenChange, thirdParties, onCreated, prefill }) => {
  const settings = useSettings();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddSeed, setQuickAddSeed] = useState("");
  const [skuRefreshKey, setSkuRefreshKey] = useState(0);

  // Reset/prefill when opening
  const handleOpenChange = (v) => {
    if (v) {
      setForm({ ...EMPTY, ...(prefill || {}) });
    }
    onOpenChange(v);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.supplier_po || !form.product_sku || !form.description) {
      toast.error("Supplier PO, SKU and Description are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.third_party_id) delete payload.third_party_id;
      // is_urgent stays as boolean even if false
      const urgent = !!payload.is_urgent;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      payload.is_urgent = urgent;
      await api.post("/bath-records", payload);
      toast.success("Bath record created");
      onCreated && onCreated();
      onOpenChange(false);
      setForm(EMPTY);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create record");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="create-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter">New Bath Record</DialogTitle>
          <DialogDescription>
            Create a manual record or use it after a Brightpearl lookup to capture local workflow data.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <Field label={`${settings.supplier_name} PO * (Supplier)`}>
            <Input value={form.supplier_po} onChange={(e) => update("supplier_po", e.target.value)} data-testid="create-supplier-po" />
          </Field>
          <Field label="Giavani PO">
            <Input value={form.internal_po} onChange={(e) => update("internal_po", e.target.value)} data-testid="create-internal-po" />
          </Field>
          <Field label="Revised / Amended PO">
            <Input
              value={form.revised_po}
              onChange={(e) => update("revised_po", e.target.value)}
              placeholder="Optional — adds a new PO without losing the original"
              data-testid="create-revised-po"
            />
          </Field>
          <Field label="Sales Order">
            <Input value={form.sales_order} onChange={(e) => update("sales_order", e.target.value)} data-testid="create-sales-order" />
          </Field>
          <Field label="Customer Reference">
            <Input value={form.customer_ref} onChange={(e) => update("customer_ref", e.target.value)} data-testid="create-customer-ref" />
          </Field>
          <Field label="Product SKU *">
            <SkuCombobox
              value={form.product_sku}
              onChange={(v) => update("product_sku", v)}
              onPick={(entry) => {
                setForm((f) => ({
                  ...f,
                  product_sku: entry.sku || f.product_sku,
                  alternate_sku: entry.alternate_sku || f.alternate_sku,
                  description: f.description || entry.description || "",
                  barcode: f.barcode || entry.barcode || "",
                }));
              }}
              onRequestQuickAdd={(typed) => {
                setQuickAddSeed(typed);
                setQuickAddOpen(true);
              }}
              refreshKey={skuRefreshKey}
              testId="create-product-sku"
            />
          </Field>
          <Field label="Alternate SKU">
            <Input
              value={form.alternate_sku}
              onChange={(e) => update("alternate_sku", e.target.value)}
              placeholder="Auto-filled from catalogue"
              className="font-mono"
              data-testid="create-alternate-sku"
            />
          </Field>
          <Field label="Barcode">
            <Input value={form.barcode} onChange={(e) => update("barcode", e.target.value)} data-testid="create-barcode" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Description *">
              <Input value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="create-description" />
            </Field>
          </div>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger data-testid="create-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Third Party">
            <Select value={form.third_party_id || "none"} onValueChange={(v) => update("third_party_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="create-third-party"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {thirdParties?.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Location">
            <Input value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="create-location" />
          </Field>
          <Field label="Brightpearl ID">
            <Input value={form.brightpearl_id} onChange={(e) => update("brightpearl_id", e.target.value)} data-testid="create-brightpearl-id" />
          </Field>
          <Field label="Customer required by">
            <Input
              type="date"
              value={form.customer_required_date ? form.customer_required_date.slice(0, 10) : ""}
              onChange={(e) => update("customer_required_date", e.target.value ? new Date(e.target.value).toISOString() : "")}
              placeholder="Customer deadline"
              data-testid="create-customer-required-date"
            />
          </Field>

          {/* Urgent flag */}
          <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50/40 p-3 flex flex-wrap items-center gap-3">
            <Flame className={`h-4 w-4 ${form.is_urgent ? "text-amber-600" : "text-zinc-400"}`} />
            <div className="flex-1 min-w-[160px]">
              <div className="text-sm font-semibold text-zinc-900">Mark as urgent</div>
              <div className="text-[11px] text-zinc-500">Highlights this record on the dashboard with an amber tint.</div>
            </div>
            <Switch checked={!!form.is_urgent} onCheckedChange={(v) => update("is_urgent", v)} data-testid="create-urgent-toggle" />
            {form.is_urgent && (
              <div className="basis-full">
                <Input
                  value={form.urgent_reason}
                  onChange={(e) => update("urgent_reason", e.target.value)}
                  placeholder="Why is this urgent? (shown on tooltip)"
                  className="mt-2"
                  data-testid="create-urgent-reason"
                />
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <Field label="Notes">
              <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} data-testid="create-notes" />
            </Field>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="create-submit">
            {submitting ? "Creating…" : "Create record"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <QuickAddSkuDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        initialSku={quickAddSeed}
        onCreated={(entry) => {
          // Auto-fill the bath record with the newly catalogued SKU's details
          setForm((f) => ({
            ...f,
            product_sku: entry.sku || f.product_sku,
            alternate_sku: entry.alternate_sku || f.alternate_sku,
            description: f.description || entry.description || "",
            barcode: f.barcode || entry.barcode || "",
          }));
          setSkuRefreshKey((n) => n + 1);
        }}
      />
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
